import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { UserAvatarFileDto } from '@workspace/data-types';
import { AvatarAuraComponent } from '../../../media';

export interface FilePreview {
  url: string | null;
  size: number;
  name: string;
}

@Component({
  selector: 'aura-file-input-preview',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatButtonModule,
    AvatarAuraComponent,
  ],
  templateUrl: './file-input-preview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileInputPreviewAuraComponent {
  control = input.required<FormControl<UserAvatarFileDto | File | null>>();
  placeholder = input.required<string>();
  filePreview = signal<FilePreview | null>(null);
  removeFile = output<void>();
  destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      const control = this.control();

      // re-subscribe whenever a new control instance is passed in
      control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((file) => {
        this.updatePreview(file);
      });

      this.updatePreview(control.value);
    });
  }

  onRemoveClick() {
    this.control().setValue(null);
    this.control().markAsDirty();
    this.filePreview.set(null);
    this.removeFile.emit();
  }

  private updatePreview(file: File | UserAvatarFileDto | null) {
    if (!file) {
      this.filePreview.set(null);
      return;
    }

    if ('url' in file) {
      this.filePreview.set({
        url: file.url,
        size: file.size,
        name: file.filename,
      });
      return;
    }

    if (file instanceof File) {
      const reader = new FileReader();
      reader.onload = () => {
        this.filePreview.set({
          url: reader.result as string,
          size: file.size,
          name: file.name,
        });
      };
      reader.readAsDataURL(file);
      return;
    }

    console.warn('Unexpected object passed to setPreview:', file);
    this.filePreview.set(null);
  }
}
